import React, { useCallback, useContext } from 'react';

import PermissionGates from 'components/PermissionGates';
import PERMISSIONS from 'permissionDefinitions';
import { Option } from 'kingpin/atoms/ContextMenu';
import ReportContext from 'contexts/ReportContext';
import ReportGadgetContext from 'contexts/ReportGadgetContext';
import EditStudioBlockOption from 'components/ReportCanvasCard/EditStudioBlockOption';
import { useCanOpenStudio } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';

// Shared, permission-gated "Edit" option for a report gadget's context menu.
// Editing a gadget is treated as managing a visualisation, so it is gated on
// REPORT_MANAGEMENT.ADD_REMOVE_VISUALISATIONS — mirroring how the dashboard
// gadget "Edit Filters" affordance is gated on DASHBOARDS.EDIT. Rendering
// nothing when there is no editable report gadget in scope keeps this a no-op
// in dashboard contexts. Where the account has Studio, the gadget is edited
// there instead, on its block (see EditStudioBlockOption).
const ReportGadgetEditOption = ({
  reportGadget: reportGadgetProp,
}: {
  reportGadget?: ReportGadgets.ReportGadget;
}) => {
  const reportContext = useContext(ReportContext);
  const reportGadgetContext = useContext(ReportGadgetContext);
  const reportGadget = reportGadgetProp || reportGadgetContext?.reportGadget;
  const { onEditReportGadget: onEditReportGadgetCallback } = reportContext;
  const canOpenStudio = useCanOpenStudio();

  const onEditReportGadget = useCallback(() => {
    if (onEditReportGadgetCallback && reportGadget) {
      onEditReportGadgetCallback(reportGadget);
    }
  }, [onEditReportGadgetCallback, reportGadget]);

  if (!onEditReportGadgetCallback || !reportGadget) {
    return null;
  }

  if (canOpenStudio) {
    return <EditStudioBlockOption />;
  }

  return (
    <PermissionGates.Has
      requiredPermission={
        PERMISSIONS.REPORT_MANAGEMENT.ADD_REMOVE_VISUALISATIONS
      }
    >
      <Option
        label="Edit"
        icon="edit-outline"
        onClick={onEditReportGadget}
        shouldHideInsteadOfCloseMenu
      />
    </PermissionGates.Has>
  );
};

export default ReportGadgetEditOption;
